import { z } from 'zod';

import { FindPaginatedPartnerCommissionsType, TransactionStatus } from '../../';
import { buildQueryString } from '../../utils/querystring';
import { makeEndpoint, paginateResponseSchema } from '../common';

import { PromoterCommissionType } from '.';

export const PartnerTransactionHistorySchema = z.object({
  id: z.string().uuid(),
  bookingId: z.string().uuid(),
  userId: z.string().uuid().optional().nullable(),
  providerBrandId: z.string().uuid(),
  promoterBrandId: z.string().uuid(),
  listingId: z.string().uuid(),
  agreementId: z.string().uuid().optional().nullable(),
  type: z.nativeEnum(PromoterCommissionType),
  transactionUSDValue: z
    .string()
    .regex(/^\d+(\.\d{1,2})?$/)
    .transform(Number),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const MinimalPartnerTransactionHistorySchema = z.object({
  id: z.string().uuid(),
  promoterBrandId: z.string().uuid(),
  amount: z
    .string()
    .regex(/^\d+(\.\d{1,2})?$/)
    .transform(Number),
  type: z.nativeEnum(PromoterCommissionType),
});

export const FindPartnerCommissionsEndpointPaginatedResponseSchema = z.object({
  partnerTransactionId: z.string(),
  amount: z.number(),
  listing: z.object({
    id: z.string(),
    title: z.string(),
  }),
  booking: z.object({
    id: z.string().uuid(),
    confirmationCode: z.string(),
    paymentMethod: z.string(),
    purchaser: z.object({
      name: z.string(),
    }),
  }),
  scheduledAt: z.coerce.date(),
  payoutDate: z.coerce.date().nullable(),
  status: z.nativeEnum(TransactionStatus),
  initiatedOn: z.coerce.date(),
});

export const FindPartnerCommissionsResponseSchema = paginateResponseSchema(
  FindPartnerCommissionsEndpointPaginatedResponseSchema,
);

const FindPartnerCommissionsEndpointPaginatedQuerySchema = z.object({
  partnerBrandId: z.string().uuid().optional(),
  type: z.nativeEnum(FindPaginatedPartnerCommissionsType),
  page: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
});

export const FindPartnerCommissionsPaginatedEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    query,
  }: {
    brandId: string;
    query: z.infer<typeof FindPartnerCommissionsEndpointPaginatedQuerySchema>;
  }) => `v1/brands/${brandId}/partners/commissions?${buildQueryString(query)}`,
  responseSchema: FindPartnerCommissionsResponseSchema,
  querySchema: FindPartnerCommissionsEndpointPaginatedQuerySchema,
  requestSchema: z.void(),
});

export const FindPartnerTransactionHistoriesByBookingResponseSchema = z.array(
  z.object({
    bookingId: z.string().uuid(),
    partnerTransactionId: z.string().uuid(),
    bookingHistoryId: z.string().uuid().nullable(),
    promoterBrandId: z.string().uuid(),
    providerBrandId: z.string().uuid(),
    type: z.nativeEnum(PromoterCommissionType),
    transactionUSDValue: z.number(),
  }),
);
